export interface WineOrder {
  date: string;
  number: string;
  author: string;
  customer: string;
  contact: string;
  salesperson: string;
  sku: string;
  status: string;
  product: string;
  origin: string;
  vintage: string;
  volume: string;
  alcohol: string;
  quantity: string;
  price: string;
  deposit: string;
  payment: string;
  description: string;
  notes: string;
}

export function orderAmounts(order: WineOrder) {
  const total = Math.round(Number(order.quantity) * Number(order.price));
  const deposit = Number(order.deposit);
  return { total, deposit, balance: total - deposit };
}

export function validateOrder(order: WineOrder): string | null {
  if (![order.date, order.number, order.author, order.customer, order.contact,
    order.salesperson, order.product, order.sku].every((value) => value.trim())) {
    return "請填寫日期、單號、填寫人、客戶、聯絡方式、業務人員與酒款資料。";
  }
  const quantity = Number(order.quantity);
  const price = Number(order.price);
  const deposit = Number(order.deposit);
  if (!Number.isSafeInteger(quantity) || quantity < 1 || quantity > 100000) return "瓶數請輸入 1～100,000 的整數。";
  if (!order.price.trim() || !Number.isFinite(price) || price <= 0 || price > 100000000) return "請輸入有效的單價。";
  if (!order.deposit.trim() || !Number.isSafeInteger(deposit) || deposit < 0) return "訂金請輸入 0 或正整數。";
  if (!Number.isSafeInteger(price)) return "單價請以新臺幣整數填寫。";
  if (deposit > orderAmounts(order).total) return "已付訂金不可超過商品總價。";
  return null;
}

const escapeHtml = (value: string) => String(value).replace(/[&<>"']/g, (char) => ({
  "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
})[char]!);

export const money = (value: number) => `NT$ ${value.toLocaleString("zh-TW")}`;

export function renderOrder(order: WineOrder): string {
  const { total, deposit, balance } = orderAmounts(order);
  const cell = (value: string) => escapeHtml(value || "—");
  const row = (label1: string, value1: string, label2: string, value2: string) =>
    `<tr><th>${cell(label1)}</th><td>${cell(value1)}</td><th>${cell(label2)}</th><td>${cell(value2)}</td></tr>`;
  return `<!doctype html><html lang="zh-Hant"><head><meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${cell(order.number)}｜酒類訂單尾款支付清單</title>
<style>
*{box-sizing:border-box}body{margin:0;background:#fff;color:#24333a;font-family:"Microsoft JhengHei","PingFang TC",sans-serif;font-size:12px;line-height:1.7}
.sheet{width:210mm;max-width:100%;margin:0 auto;padding:15mm 17mm 12mm}
header{display:flex;justify-content:space-between;align-items:flex-end;padding-bottom:16px;border-bottom:2px solid #087f70;margin-bottom:20px}
.brand{font-size:12px;letter-spacing:2px;color:#087f70;font-weight:700}h1{font-size:25px;letter-spacing:3px;font-weight:500;margin:7px 0 0}.label{font-size:10px;color:#78888c;letter-spacing:2px}
.number{text-align:right;font-size:11px}h2{font-size:12px;font-weight:600;color:#087f70;margin:18px 0 8px;letter-spacing:1px}
table{width:100%;border-collapse:collapse;table-layout:fixed}th,td{border:1px solid #b9c8c7;padding:11px 10px;vertical-align:middle;text-align:left;overflow-wrap:anywhere;white-space:pre-wrap}
th{width:22%;background:#f5f8f7;font-weight:500;color:#627572}td{width:28%}.description td{min-height:60px}.balance{background:#edf8f4;color:#087f70;font-size:19px;font-weight:700}
.notes{margin-top:12px;min-height:65px;border:1px solid #b9c8c7;padding:10px;white-space:pre-wrap;overflow-wrap:anywhere}.notes b{display:block;font-weight:500;color:#627572;margin-bottom:5px}
.foot{display:flex;justify-content:space-between;border-top:1px solid #d7e1de;margin-top:22px;padding-top:10px;color:#879793;font-size:10px;gap:12px}
@page{size:A4;margin:0}@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}.sheet{max-width:none}table,tr,.notes{break-inside:avoid}header{break-after:avoid}}
</style></head><body><article class="sheet">
<header><div><div class="brand">貨物管理平台</div><h1>尾款支付清單</h1></div><div class="number"><div class="label">WINE ORDER</div>${cell(order.number)}</div></header>
<table aria-label="清單資料">${row("清單日期", order.date.replaceAll("-", "/"), "清單編號", order.number)}${row("填寫人", order.author, "訂單狀態", order.status)}</table>
<h2>01 ／ 客戶資訊</h2><table aria-label="客戶資訊">${row("客戶名稱", order.customer, "聯絡方式", order.contact)}${row("專屬業務人員", order.salesperson, "商品編號", order.sku)}</table>
<h2>02 ／ 訂單與酒款資訊</h2><table aria-label="酒款資訊">${row("酒款名稱", order.product, "產地／年份", [order.origin, order.vintage].filter(Boolean).join(" ／ "))}${row("容量／酒精濃度", [order.volume, order.alcohol].filter(Boolean).join(" ／ "), "數量", `${order.quantity} 瓶`)}${row("酒款說明", order.description, "訂單狀態", order.status)}</table>
<h2>03 ／ 金額資訊</h2><table aria-label="金額資訊">${row("每瓶單價", money(Number(order.price)), "商品總價", money(total))}${row("支付方式", order.payment, "已付訂金", money(deposit))}<tr><th colspan="2">應付尾款金額</th><td class="balance" colspan="2">${cell(money(balance))}</td></tr></table>
<div class="notes"><b>備註</b>${cell(order.notes)}</div>
<footer class="foot"><span>貨物管理平台 ／ 酒類訂單</span><span>本清單為訂單付款明細，非付款完成證明。</span></footer>
</article></body></html>`;
}
